import routes from './routes.ts'

// RouteMap is a strongly typed map from the routes using the route pattern as a key
// E.g., { '/projects': {route for /projects} }

type InferRouteMap<T extends typeof routes> = {
  [K in T[number] as K[0]]: K[1]
}

const buildRouteMap = <T extends typeof routes>(routes: T): InferRouteMap<T> =>
  routes.reduce<Record<string, unknown>>((map, [pattern, route]) => {
    map[pattern] = route
    return map
  }, {}) as InferRouteMap<T>

const routeMap = buildRouteMap(routes)

// In order to more simply use routes from the routeMap, a strongly typed route function is needed to instantiate the route function to avoid confusion in the wild.
// Using the route directly from the routeMap would involve accessing the the route by key and also having to instantiate it, which might be confusing.
// Note: The reason route registrations are a function is because the fluent route building requires state, so each route usage needs to be instantiated.
//
// #Potentially confusing  ----v
// routeMap['/offers/:offerId']().params({ offerId: '123' })
//
// The route function effectively returns the route by key, instantiates it, while upholding type integrity.
//
// #Easier to reason about
// route('/offers/:offerId').params({ offerId: '123' })

type RouteKey = keyof typeof routeMap

type Route<K extends RouteKey> = ReturnType<(typeof routeMap)[K]>

type AnyRoute = Route<RouteKey>

const route = <K extends RouteKey>(key: K): Route<K> => {
  return routeMap[key]() as Route<K>
}

const findRoute = (localPath: string | undefined): AnyRoute | undefined => {
  if (localPath) {
    const route = Object.values(routeMap).find((route) =>
      route().isMatch(localPath)
    )
    return route ? route() : undefined
  }
}

const getProtectedRouteRoots = () => {
  return Array.from(
    new Set(
      Object.entries(routeMap)
        .filter(
          ([routeKey, routeValue]) =>
            routeValue().isProtected &&
            !routeValue().isTest &&
            routeKey.length > 1
        )
        .map(([, routeValue]) => routeValue().getRoot())
    )
  )
}

type ExtractQueryParams<K extends RouteKey> = Parameters<Route<K>['query']>[0]

export {
  route,
  type RouteKey,
  type AnyRoute,
  type Route,
  findRoute,
  getProtectedRouteRoots,
  type ExtractQueryParams,
}
